/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import {
  formatAccumulation,
  formatFilterValues,
  formatNum,
  formatPercentNum, generateCopyData,
  getUpAndDownStatus,
  getWeekDay,
} from '@/pages/dashboard/utils/utils';
import { PagingQuery, SortBy } from "@/pages/dashboard/pages/overview/components/OverviewTable/types";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { Bubble } from "tea-component";
import { getMultiSortParams } from "@/pages/dashboard/components/OverallTableCo/utils";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([])
  // 总条数
  const [total, setTotal] = useState(0)

  // 表格数据
  const [tableData, setTableData] = useState([])

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([])
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  }
}

// 表格上传参数
export function getTableRequestParams({ gameCode, filterValue: formVal, accumulated, sort, paging, selectType, ...resetParams }) {
  const { rangeTime, zone, region, system, channel, language, filterValue, newFilter } = formVal;
  const { regions, markets } = region || {};
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  let params = {
    game_code: gameCode,
    // 筛选器上传参数
    start_date: rangeTime?.[0], // TODO overview页面时间特殊处理!!!
    end_date: rangeTime?.[1],
    filters: {
      zone_list: zone,
      market_list: markets,
      os_list: system,
      channel_list: channel,
      lang_list: language,
    },
    // 维度筛选器上传参数
    accumulation:formatAccumulation(accumulated),
    // 分页
    page: paging.pageIndex,
    page_size: paging.pageSize,
    // 排序
    // sorted_by: order ? by : '',
    // desc: !!(order && order === 'desc')
    sorted_by: by?.replace(selectType, ''),
    desc: order === 'desc',
    multi_sort: getMultiSortParams([], [], {by, order}, selectType, true),
  }

  if (newFilter) {
    const { rangeTime, ...filters } = filterValue;
    const [start, end] = rangeTime;
    params = {
      ...params,
      filters: formatFilterValues(filters),
      start_date: start,
      end_date: end
    }
  }

  params = {
    ...params,
    ...resetParams
  }

  return params;
}

export function useTableColumns() {
  const { formatMessage } = useIntl();
  const isEn = getLocale() === 'en-US'
  const DATE_COLUMN = {
    key: 'date',
    copyKey:'date',
    header: formatMessage({ id: "common_date" }),
    width: 160,
    fixed: "left",
    align: "left",
    render(record:any) {
      /** Sum提示需要用到useState，
      *所以拿出来单独处理，
      *不然render里拿不到最新的的state
      */
      return <DateCell record={record} formatMessage={formatMessage} />;
    }    
  }

  // 日期列, 周六日的颜色高亮 & Sum行增加提示
  function DateCell({ record, formatMessage }) {
    const [isIconHover, setIsIconHover] = useState(false);
    const locale = getLocale()

    const reg = /\((.+?)\)/gi;
    const weekValue = record?.date?.match(reg)?.[0]?.replace(/^\(/, "")?.slice(0, -1);
    let weekStr = '';
    if (weekValue) {
      if (weekValue?.includes('Null') || weekValue?.includes('去空')) {
        weekStr = `(${weekValue})`;
      } else {
        weekStr = weekValue;
      }
    }
    const dateVal = record?.date ? record?.date?.split('(') : ['-'];
    const isSunOrSat = weekStr === 'Sun' || weekStr === 'Sat' || weekStr === '周日' || weekStr === '周六'
    const showSumTip = record.date === 'Sum' || record.date === '汇总值';

    return (
      <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
        <span className='table-text'>{dateVal?.[0]}</span>
        <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
          {weekStr}
          {showSumTip && (
            <div className='port-item'>
              <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
                <Bubble
                  dark
                  placement="top-start"
                  arrowPointAtCenter
                  style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                  content={formatMessage({ id: 'overview_sum_tip' })}
                >
                  <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
                </Bubble>
              </div>
            </div>
          )}
        </span>
      </div>
    );
  }

  // Accumulate 列
  const getAccumulateColumns = (accumulates: string[] = []) => {
    // if (accumulates[0] === ACCUMULATED_VALUE) return [];
    const columns = [];
    accumulates.forEach((value: string, i) => {
      // @ts-ignore
      if (i > 0) {
        return columns.push({
          key: value,
          copyKey:value,
          header: formatMessage({ id: `common_${value}` }),
          align: 'left',
          width: 200,
          render(record) {
            return record.dimension_name?.[`${value}_name_${isEn ? 'en' : 'cn'}`] || "-";
          }
        });
      }
    })

    return columns;
  }

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey, metricsInfo, explainInfo, selectType) => {
    const isShowUpDown = selectType !== "value_only";
    return metricsKey.reduce((columns, key) => {
      const info = metricsInfo[key];
      const { rainbowKey, explainEn, explainCn } = info || {};

      const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      // @ts-ignore
      columns.push(
        {
          key: key + selectType,
          copyKey:key,
          selectType:selectType,
          header: info?.name,
          align: "right",
          explain,
          width: isShowUpDown ? info?.width : info?.onlyValueWidth,
          isPercent: info?.isPercent,
          render(record) {
            const metric = record?.metrics?.[key] || {};
            const { value, dod, progressWidth, metricValue, status, } = metric;

            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                {/* <div className={'table-text-bg'} style={{width: `${progressWidth}%`}} /> */}
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>)

          }
        },
      );
      return columns;
    }, []);
  }

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
  }
}

/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: {gameCode?: any, selector?: any}) => {
  const isEn = getLocale() === 'en-US'
  if (!extendConfig) {
    const {dimension_name} = data || {};
    if (dimension_name) {
      Object.keys(data).forEach(key => {
        const value = data[key]
        data[key] = dimension_name?.[`${key}_${isEn ? 'en' : 'cn'}`] || value
      })
    }
  } else {
    data = generateCopyData(data, {gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector})
  }
  return data
}

// 表格返回数据格式整理
export function useTableDataFormat(tableData, metricsInfo, timeType = "Day", isCopyData?: boolean, extendConfig?: {gameCode?: any, selector?: any}) {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    if (!Array.isArray(tableData)) return []
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig)
      }
      const { date, metrics, type, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      // const dateStr = timeType === "Month" ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD") + getWeekDay(date, WEEK_DAY_ARR);
      let dateStr = getDateFormatString(timeType, date) // 要显示周一日期
      dateStr = timeType === "Month" ? dateStr : dateStr + getWeekDay(dateStr, WEEK_DAY_ARR);

      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        // if (!metricsInfo[key]) return; // 后端会返回全量指标的数据, 而前端页面可能只展示部分指标
        const { value, dod, sdlw } = metrics[key] || { value: "", dod: "", sdlw: "" }
        const { isPercent, decimals, isUserType, dodDecimals } = metricsInfo[key] // TODO 如果指标信息的配置对象格式不同, 导致逻辑无法复用
        const progressWidth = isPercent ? value * 100 : 0; // 单元格内进度条的宽度
        const metricValue = isPercent ? formatPercentNum(value, decimals, false) : formatNum(value, false, isUserType);
        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const dodPercentStr = formatPercentNum(dod, dodDecimals, true)
        const sdlwPercentStr = formatPercentNum(sdlw, dodDecimals, true)

        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus,
          sdlwStatus,
          dodPercentStr,
          sdlwPercentStr,
          ...metrics[key]
        }
      })

      return {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: timeType === "Day" && (moment(date).format('E') === '6' || moment(date).format('E') === '7'),
        metrics,
        ...accumulates
      }
    })
    // if (customFormat) return customFormat(newData); // 为了配合外部的自定义格式整理
    // return newData;
  }, [tableData, getLocale()])
}
// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(statisticsData, metricsInfo, showStatistic,isCopyData) {
  const { formatMessage } = useIntl();
  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {}
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${type}` }) // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        const { value, dod } = metrics[key] || { value: "", dod: "" }
        const { isPercent, decimals, isUserType } = metricsInfo[key] // TODO 如果指标信息的配置对象格式不同, 导致逻辑无法复用
        const progressWidth = isPercent && type !== 'count' ? value * 100 : 0; // 单元格内进度条的宽度
        const metricValue = isCopyData ?value : isPercent && type !== 'count' ? formatPercentNum(value, decimals, false) : formatNum(value, false, type == 'count' ? true : isUserType);
        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus: "no",
          sdlwStatus: "no",
          dodPercentStr: "",
          sdlwPercentStr: "",
          ...metrics[key]
        }
      })

      acc[type] = {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        metrics,
        ...accumulates
      }
      return acc;
    }, {})
  }, [statisticsData, getLocale()])
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD")
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
}
